import {useEffect} from "react";
import {Box, CircularProgress} from "@mui/material";
import {useAmplifyConfig} from "../../hooks/useAmplifyConfig";
import {useAuthContext} from "../../contexts/AuthContext";
import {isUndefined} from "lodash";
import {Navigate, useLocation, useNavigate} from "react-router-dom";
import {urls} from "../urls";
import {isInternalUser} from "../../utils/userUtils";
import {useGetPartnersGroup} from "../../api/requests/partner-level/getPartnersGroup";
import {toast} from "sonner";

interface GuardProps {
  children: React.ReactNode;
}

export const Guard = ({children}: GuardProps) => {
  const {isLoggedIn, setPartnerCode, partnerCode, username, setPartnersGroup, partnersGroup, session} =
    useAuthContext();
  const currentPartnerId = partnerCode || sessionStorage.getItem("partnerId") || undefined;
  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();

  useEffect(() => {
    setPartnerCode(currentPartnerId);
  }, []);

  const shouldFetchPartnersGroup =
    isLoggedIn &&
    !!session?.tokens?.accessToken &&
    !!username &&
    !isInternalUser(username) &&
    partnersGroup.length === 0;

  const {
    data: fetchedPartnersGroup,
    isLoading,
    error: partnersGroupError,
  } = useGetPartnersGroup({
    enabled: shouldFetchPartnersGroup === true,
  });

  const isCheckingGroup = shouldFetchPartnersGroup === true && isLoading;

  useEffect(() => {
    if (fetchedPartnersGroup && fetchedPartnersGroup.length > 0) {
      setPartnersGroup(fetchedPartnersGroup);
      if (fetchedPartnersGroup.length === 1) {
        const partnerCode = fetchedPartnersGroup[0].code;
        setPartnerCode(partnerCode);
        sessionStorage.setItem("partnerId", partnerCode);
      } else if (fetchedPartnersGroup.length > 1 && !currentPartnerId) {
        navigate(urls.partnerSelector.url, {state: {origin: pathname + search}});
      }
    }
  }, [fetchedPartnersGroup, setPartnersGroup, setPartnerCode, currentPartnerId, navigate, pathname, search]);

  useEffect(() => {
    if (partnersGroupError) {
      toast.error("Failed to load partner information. You may proceed but some features may be limited.");
    }
  }, [partnersGroupError]);

  useEffect(() => {
    if (isLoggedIn) {
      if (isInternalUser(username) && !currentPartnerId) {
        navigate(urls.partnerSelector.url, {state: {origin: state?.origin}});
      } else if (state?.origin) {
        navigate(state.origin);
        // Temporary fix to allow redirect. Remove after refactoring:
        // https://www.notion.so/bounceai/Refactor-routing-1dc43312447980e693fac302c98a5c45?pvs=4
      } else if (pathname === urls.home.url) {
        navigate(urls.dashboard.url, {replace: true});
      }
    }
  }, [navigate, username, isLoggedIn, currentPartnerId]);

  useAmplifyConfig();

  if (isUndefined(isLoggedIn) || isCheckingGroup) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!isLoggedIn) {
    return <Navigate to={urls.auth.url} state={{origin: pathname + search}} />;
  }

  return <>{children}</>;
};
